import React from 'react';

/**
 * Print-only running header that repeats on every printed page.
 *
 * Visibility is controlled by the body class set by the parent page:
 *   - `body.patient-print-mode .print-running-header`   (PatientProfile)
 *   - `body.submission-print-mode .print-running-header` (FormSubmissionEditPage)
 *
 * Hidden on screen (the CSS in /app/frontend/src/index.css starts each rule
 * with `display: none` and only un-hides when the body has the right class
 * during print).  The component is a thin wrapper so both call sites share
 * the same patient summary layout.
 *
 * Props:
 *   patient — full patient object
 *   form?   — optional form name to show after the patient's identifiers
 *   note?   — optional small note (e.g. "Page X of Y" — browsers add this themselves)
 */
export default function PrintPatientHeader({ patient, form = null, note = null }) {
  if (!patient) return null;
  const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || 'Patient';
  // Build the data rows — split into two compact rows so the header stays tight.
  const row1 = [
    ['Name', fullName, true],
    ['DOB', patient.date_of_birth || '—'],
    ['MRN', patient.mrn || '—'],
    ['Gender', patient.gender || '—'],
    ['Phone', patient.phone || '—'],
  ];
  const row2 = [
    ['Email', patient.email || '—'],
    ['Insurance', patient.insurance_provider || '—'],
    ['Ins. ID', patient.insurance_id || '—'],
    ['Practitioner', patient.medical_practitioner || '—'],
    ['Diagnosis', patient.medical_diagnosis || '—'],
  ];

  const Cell = ([label, value, bold = false]) => (
    <div className="flex items-baseline gap-1 min-w-0" key={label}>
      <span className="text-[#64748B] uppercase tracking-wider text-[8px] font-medium">{label}:</span>
      <span className={`truncate text-[10px] text-[#0A2342] ${bold ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  );

  return (
    <div className="print-running-header" data-testid="print-running-header">
      <div className="flex flex-col gap-0.5 w-full">
        {/* Top: form name + identifiers row */}
        <div className="flex items-center justify-between gap-3 border-b border-[#E2E8F0] pb-1">
          <div className="text-[11px] font-bold text-[#0E7490] uppercase tracking-wider">{fullName}</div>
          {form && <div className="text-[10px] text-[#64748B] truncate">{form}</div>}
          {note && <div className="text-[9px] text-[#94A3B8]">{note}</div>}
        </div>
        {/* Two compact info rows */}
        <div className="grid grid-cols-5 gap-x-3 gap-y-0.5">
          {row1.map(Cell)}
        </div>
        <div className="grid grid-cols-5 gap-x-3 gap-y-0.5">
          {row2.map(Cell)}
        </div>
      </div>
    </div>
  );
}
